// Subida de fotos de actividad y logo de empresa.
// Contrato: docs/CONTRATO-API-FASE1.md (backend: routes/upload.py).

export const MAX_ACTIVITY_IMAGES = 5;

export const TOO_LARGE_MESSAGE =
  'La foto es demasiado pesada. Elige otra más ligera o redúcela antes de subirla.';

// code: 'image_too_large' (400 del backend, se puede reintentar con autoResize),
// 'payload_too_large' (cualquier 413, incluido el HTML de nginx) u otro.
export class UploadError extends Error {
  constructor(message, { code = null, status = 0, files = [] } = {}) {
    super(message);
    this.name = 'UploadError';
    this.code = code;
    this.status = status;
    this.files = files;
  }
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('empresa-token')}`,
});

// nginx responde su propio 413 en HTML, así que el cuerpo puede no ser JSON.
const parseResponse = async (response) => {
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  if (response.status === 413) {
    throw new UploadError(TOO_LARGE_MESSAGE, { code: 'payload_too_large', status: 413 });
  }

  if (!response.ok || !data || data.success === false) {
    throw new UploadError(
      (data && data.error) || 'No se pudo subir la imagen. Inténtalo de nuevo.',
      { code: data && data.code, status: response.status, files: (data && data.files) || [] }
    );
  }
  return data;
};

const postFile = async (url, field, file, autoResize) => {
  const formData = new FormData();
  formData.append(field, file);
  if (autoResize) formData.append('autoResize', 'true');

  let response;
  try {
    response = await fetch(url, { method: 'POST', headers: authHeaders(), body: formData });
  } catch (e) {
    throw new UploadError('Error de conexión. Revisa tu internet e inténtalo de nuevo.');
  }
  return parseResponse(response);
};

// Una foto por petición (5 fotos de 5 MB no caben en el límite de 12 MB).
// Devuelve la URL relativa (/uploads/activities/...).
export const uploadActivityImage = async (file, { autoResize = false } = {}) => {
  const data = await postFile('/api/upload/activity-images', 'images', file, autoResize);
  return data.imageUrls[0];
};

export const uploadEmpresaLogo = async (file, { autoResize = false } = {}) => {
  const data = await postFile('/api/upload/empresa-logo', 'logo', file, autoResize);
  return data.logoUrl;
};

export const deleteEmpresaLogo = async () => {
  let response;
  try {
    response = await fetch('/api/upload/empresa-logo', {
      method: 'DELETE',
      headers: authHeaders(),
    });
  } catch (e) {
    throw new UploadError('Error de conexión. Revisa tu internet e inténtalo de nuevo.');
  }
  await parseResponse(response);
};

// Sube un archivo y, si el backend dice que pesa más de 5 MB, pregunta si se
// redimensiona y reintenta con autoResize=true. `upload(file, opts)` es
// uploadActivityImage o uploadEmpresaLogo. `askResize(fileName)` devuelve una
// promesa de booleano (el modal de la marca, vía useConfirm). Devuelve null si
// el usuario declina.
export const uploadWithResizePrompt = async (upload, file, askResize) => {
  try {
    return await upload(file);
  } catch (error) {
    if (error.code !== 'image_too_large') throw error;
    const name = error.files[0] || file.name;
    const accepted = await askResize(name);
    if (!accepted) return null;
    return upload(file, { autoResize: true });
  }
};

// Texto del modal "¿Redimensionar automáticamente?" (lo usan ImageUploader y Settings).
export const resizeConfirmOptions = (fileName) => ({
  title: '¿Redimensionar automáticamente?',
  message: `"${fileName}" pesa más de 5 MB. Podemos reducir su tamaño automáticamente para subirla, sin que tengas que editarla.`,
  confirmLabel: 'Redimensionar',
  cancelLabel: 'Elegir otra foto',
});
